import type { BusinessDocumentPipelineBundle } from '../../core/services/business-document.service';

/** The executed route to the current step, followed by its default forward path. */
export function pipelinePath(
  bundle: BusinessDocumentPipelineBundle,
  currentId: string | null | undefined,
  completedInventoryStepIds?: string[],
): string[] {
  const completed = completedInventoryStepIds === undefined ? null : new Set(completedInventoryStepIds);
  const steps = bundle.steps.filter(item => item.step.active || completed?.has(item.step.id))
    .sort((a, b) => a.step.sequence - b.step.sequence);
  if (!steps.length) return [];
  const initialId = steps.find(item => item.step.is_initial)?.step.id ?? steps[0].step.id;
  const targetId = currentId ?? initialId;
  const activeIds = new Set(steps.map(item => item.step.id));
  const inventoryIds = new Set(steps.filter(item => item.step.step_type === 'inventory_movement').map(item => item.step.id));
  const transitions = bundle.transitions.filter(edge => edge.active && edge.from_step_id
    && activeIds.has(edge.from_step_id) && activeIds.has(edge.to_step_id))
    .sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.sequence - b.sequence);
  const queue: string[][] = [[initialId]];
  const visited = new Set<string>();
  let selected: string[] | null = null;
  while (queue.length) {
    const path = queue.shift()!;
    const cursor = path.at(-1)!;
    const historyKey = completed ? [...completed].filter(id => path.includes(id)).join(',') : '';
    const key = `${cursor}|${historyKey}`;
    if (visited.has(key)) continue;
    visited.add(key);
    if (cursor === targetId && (!completed || [...completed].every(id => path.includes(id)))) {
      selected = path;
      break;
    }
    for (const edge of transitions.filter(edge => edge.from_step_id === cursor)) {
      if (path.includes(edge.to_step_id)) continue;
      // Once paths converge, a skipped Inventory branch must not reappear.
      if (completed && inventoryIds.has(edge.to_step_id) && edge.to_step_id !== targetId && !completed.has(edge.to_step_id)) continue;
      queue.push([...path, edge.to_step_id]);
    }
  }
  const path = selected ?? (completed
    ? [...new Set([initialId, ...completed, targetId])].filter(id => activeIds.has(id))
    : [activeIds.has(targetId) ? targetId : initialId]);
  const seen = new Set(path);
  let cursor = path.at(-1)!;
  while (cursor) {
    const next = transitions.find(edge => edge.from_step_id === cursor && edge.is_default);
    if (!next || seen.has(next.to_step_id)) break;
    path.push(next.to_step_id);
    seen.add(next.to_step_id);
    cursor = next.to_step_id;
  }
  return path;
}
